'use client';
import React, { useEffect, useRef, useState, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon } from 'lucide-react';

const FEATURES = [
  { icon: '✦', text: 'Unlimited bookings' },
  { icon: '✦', text: 'No-show protection' },
  { icon: '✦', text: 'Last-minute cancellation protection' },
  { icon: '✦', text: 'Automated reminders' },
  { icon: '✦', text: 'Customer management' },
  { icon: '✦', text: 'Multiple services per booking' },
  { icon: '✦', text: 'Payments tracking' },
  { icon: '✦', text: 'Adjustable pricing & rates' },
  { icon: '✦', text: 'optional rescheduling charge' },
  { icon: '✦', text: 'No added commission or fee' },
  { icon: '✦', text: 'Instant fund collection — clients pay at booking, you receive it right away' },
  { icon: '✦', text: 'Customizable services duration & availability' },
  { icon: '✦', text: '...and many more' },
];

function DemoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="relative w-full max-w-lg rounded-2xl p-8 border"
        style={{ background: '#1A1410', borderColor: 'rgba(201,169,110,0.3)', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
          style={{ color: 'rgba(245,240,232,0.5)' }}
        >
          ✕
        </button>
        <div className="flex items-center gap-3 mb-6">
          <AppLogo size={32} />
          <span className="font-bold text-lg" style={{ fontFamily: 'var(--font-serif)' }}>
            <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>
            {' '}Demo
          </span>
        </div>
        <h3 className="font-bold text-xl mb-2" style={{ color: '#F5F0E8', fontFamily: 'var(--font-serif)' }}>
          Welcome to the Demo Trial
        </h3>
        <p className="text-sm mb-6" style={{ color: 'rgba(245,240,232,0.6)' }}>
          Explore <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span> as a business owner. This demo gives you full access to the dashboard with sample data — no sign-up required.
        </p>
        <div className="space-y-3 mb-6">
          {[
            { icon: '✦', title: 'Dashboard & Analytics', desc: 'View bookings, revenue, and no-show stats' },
            { icon: '✦', title: 'Client Booking Flow', desc: 'See exactly what your clients experience' },
            { icon: '✦', title: 'Settings & Protection', desc: 'Configure fees, availability, and reminders' },
          ].map((item, i) => (
            <div key={`demo-${i}`} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.15)' }}>
              <span style={{ color: '#D4AF37', fontSize: '1rem', marginTop: 1 }}>{item.icon}</span>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#E8C99A' }}>{item.title}</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.5)' }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            href="/business-owner-dashboard"
            className="flex-1 text-center py-3 rounded-full font-bold text-sm transition-all"
            style={{ background: 'linear-gradient(135deg, #C9A96E, #8B4513)', color: '#F5F0E8', boxShadow: '0 6px 24px rgba(201,169,110,0.35)' }}
            onClick={onClose}
          >
            Enter Owner Dashboard →
          </Link>
          <Link
            href="/client-booking-page"
            className="flex-1 text-center py-3 rounded-full font-bold text-sm transition-all border"
            style={{ borderColor: 'rgba(201,169,110,0.4)', color: '#C9A96E', background: 'rgba(201,169,110,0.08)' }}
            onClick={onClose}
          >
            View Client Booking
          </Link>
        </div>
        <p className="text-xs text-center mt-4" style={{ color: 'rgba(245,240,232,0.35)' }}>
          Ready to get started?{' '}
          <Link href="/sign-up-login" className="underline" style={{ color: '#C9A96E' }} onClick={onClose}>
            Create your free account
          </Link>
        </p>
      </div>
    </div>
  );
}

function LandingPageInner() {
  const [visible, setVisible] = useState(false);
  const [taglineVisible, setTaglineVisible] = useState(false);
  const [plansVisible, setPlansVisible] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const plansRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const searchParams = useSearchParams();

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => setTaglineVisible(true), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    // Auto-scroll to plans if redirected from subscription check
    if (searchParams?.get('plans') === '1') {
      setTimeout(() => {
        plansRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 800);
    }
  }, [searchParams]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setPlansVisible(true); },
      { threshold: 0.15 }
    );
    if (plansRef?.current) observer?.observe(plansRef?.current);
    return () => observer?.disconnect();
  }, []);

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const bg = isDark ? '#0D0B09' : '#FAF7F2';
  const textBase = isDark ? '#F0EBE3' : '#2C1810';
  const textMuted = isDark ? 'rgba(240,235,227,0.55)' : 'rgba(44,24,16,0.55)';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(44,24,16,0.04)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(44,24,16,0.1)';
  const subheadingColor = isDark ? 'rgba(240,235,227,0.82)' : 'rgba(44,24,16,0.78)';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: bg, color: textBase, fontFamily: "'DM Sans', sans-serif" }}>
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}

      {/* Animated gradient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div
          className="absolute rounded-full blur-3xl"
          style={{
            width: '60vw', height: '60vw',
            top: '-20vw', left: '-10vw',
            opacity: isDark ? 0.25 : 0.1,
            background: 'radial-gradient(circle, #C9A96E 0%, #8B4513 50%, transparent 70%)',
            animation: 'orbFloat1 12s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full blur-3xl"
          style={{
            width: '50vw', height: '50vw',
            bottom: '-15vw', right: '-10vw',
            opacity: isDark ? 0.18 : 0.08,
            background: 'radial-gradient(circle, #3D1A0E 0%, #C9A96E 50%, transparent 70%)',
            animation: 'orbFloat2 15s ease-in-out infinite',
          }}
        />
      </div>

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-2.5">
          <AppLogo size={32} />
          <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'var(--font-serif)' }}>
            <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full transition-all hover:opacity-80"
            style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(44,24,16,0.08)', color: isDark ? '#E8C99A' : '#8B4513' }}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link
            href="/sign-up-login"
            className="text-sm font-semibold px-4 py-2 rounded-full transition-all"
            style={{ color: isDark ? '#E8C99A' : '#8B4513' }}
          >
            Sign In
          </Link>
          <button
            onClick={scrollToPlans}
            className="text-sm font-semibold px-4 py-2 rounded-full transition-all border"
            style={{
              borderColor: isDark ? 'rgba(201,169,110,0.5)' : 'rgba(139,69,19,0.4)',
              color: isDark ? '#C9A96E' : '#8B4513',
              background: isDark ? 'rgba(201,169,110,0.08)' : 'rgba(139,69,19,0.06)',
            }}
          >
            Subscribe
          </button>
          <button
            onClick={() => setShowDemo(true)}
            className="text-sm font-bold px-5 py-2.5 rounded-full transition-all"
            style={{
              background: 'linear-gradient(135deg, #C9A96E, #8B4513)',
              color: '#F5F0E8',
              boxShadow: '0 4px 20px rgba(201,169,110,0.35)',
            }}
          >
            Try Demo
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-14 pb-24 md:pt-20 md:pb-32">

        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border mb-10"
          style={{
            background: isDark ? 'rgba(201,169,110,0.1)' : 'rgba(201,169,110,0.12)',
            borderColor: 'rgba(201,169,110,0.35)',
            color: isDark ? '#C9A96E' : '#8B4513',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
          Built for independent service professionals
        </div>

        {/* BIG SchedSure title */}
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(4rem, 12vw, 9.5rem)',
            fontWeight: 800,
            lineHeight: 1.0,
            letterSpacing: '-0.03em',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s',
            position: 'relative',
          }}
        >
          <span
            className="schedsure-sparkle"
            style={{
              background: 'linear-gradient(90deg, #C9A96E 0%, #F5E6C8 30%, #C9A96E 50%, #F5E6C8 70%, #C9A96E 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmerTitle 3.5s linear infinite',
              display: 'inline',
            }}
          >Sched</span><span
            className="schedsure-sparkle"
            style={{
              background: 'linear-gradient(90deg, #8B4513 0%, #D4956A 30%, #8B4513 50%, #D4956A 70%, #8B4513 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmerTitle 3.5s linear infinite 0.4s',
              display: 'inline',
            }}
          >sure</span>
        </h1>

        {/* Subheading phrase — all one color except SchedSure */}
        <div
          className="mt-7 max-w-3xl"
          style={{
            opacity: taglineVisible ? 1 : 0,
            transform: taglineVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.1rem, 2.8vw, 1.65rem)',
              fontStyle: 'italic',
              fontWeight: 600,
              lineHeight: 1.45,
              color: subheadingColor,
            }}
          >
            No-shows? Last-minute cancellations?{' '}
            <br className="hidden sm:block" />
            With{' '}
            <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>
            <span style={{ color: subheadingColor }}>, you stay profitable for sure.</span>
          </p>
        </div>

        {/* CTA buttons — Try Demo + Subscribe side by side */}
        <div
          className="flex flex-col sm:flex-row gap-4 mt-10"
          style={{
            opacity: taglineVisible ? 1 : 0,
            transform: taglineVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s',
          }}
        >
          <button
            onClick={() => setShowDemo(true)}
            className="px-9 py-4 rounded-full font-bold text-base transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #C9A96E, #8B4513)',
              color: '#F5F0E8',
              boxShadow: '0 8px 32px rgba(201,169,110,0.4)',
              letterSpacing: '0.01em',
            }}
          >
            Try Demo →
          </button>
          <button
            onClick={scrollToPlans}
            className="px-9 py-4 rounded-full font-bold text-base transition-all hover:scale-105 border-2"
            style={{
              borderColor: isDark ? 'rgba(201,169,110,0.5)' : 'rgba(139,69,19,0.4)',
              color: isDark ? '#C9A96E' : '#8B4513',
              background: isDark ? 'rgba(201,169,110,0.08)' : 'rgba(139,69,19,0.06)',
              letterSpacing: '0.01em',
            }}
          >
            Subscribe
          </button>
        </div>

        {/* Description paragraph */}
        <div
          className="mt-10 max-w-2xl"
          style={{
            opacity: taglineVisible ? 1 : 0,
            transform: taglineVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.25s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.25s',
          }}
        >
          <p
            style={{
              fontSize: 'clamp(0.9rem, 1.8vw, 1.05rem)',
              lineHeight: 1.75,
              color: textMuted,
              fontWeight: 400,
            }}
          >
            No-shows and last-minute cancellations cost businesses time and money.{' '}
            <span style={{ color: isDark ? '#C9A96E' : '#8B4513', fontWeight: 600 }}>
              <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>
            </span>{' '}
            was built to solve that problem. Our platform automates appointment scheduling, protects your business from unreliable bookings, and creates a smoother experience for both businesses and clients. Simply set up your services once, and{' '}
            <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>{' '}
            handles the rest automatically.
          </p>
        </div>
      </section>

      {/* OUR PLANS */}
      <section ref={plansRef} className="relative z-10 px-6 md:px-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="font-bold mb-3 tracking-widest uppercase"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                color: textBase,
                letterSpacing: '0.12em',
                opacity: plansVisible ? 1 : 0,
                transform: plansVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              OUR PLANS
            </h2>
            <p
              className="text-base"
              style={{
                color: textMuted,
                opacity: plansVisible ? 1 : 0,
                transition: 'opacity 0.7s ease 0.1s',
              }}
            >
              Everything you need to protect your income and grow your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly plan */}
            <div
              className="rounded-2xl p-8 border flex flex-col"
              style={{
                background: cardBg,
                borderColor: cardBorder,
                opacity: plansVisible ? 1 : 0,
                transform: plansVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s',
              }}
            >
              <div className="mb-6">
                <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C9A96E' }}>Monthly</p>
                <div className="flex items-end gap-1.5">
                  <span className="font-bold" style={{ fontFamily: 'var(--font-serif)', fontSize: '3.5rem', color: textBase, lineHeight: 1 }}>$29</span>
                  <span className="text-base mb-2" style={{ color: textMuted }}>/month</span>
                </div>
                <p className="text-sm mt-2" style={{ color: textMuted }}>Billed monthly. Cancel anytime.</p>
              </div>

              <ul className="space-y-3 flex-1 mb-8">
                {FEATURES?.map((f, i) => (
                  <li key={`mf-${i}`} className="flex items-center gap-3 text-sm" style={{ color: isDark ? 'rgba(240,235,227,0.8)' : 'rgba(44,24,16,0.8)' }}>
                    <span className="text-base" style={{ color: '#D4AF37' }}>{f?.icon}</span>
                    {f?.text}
                  </li>
                ))}
              </ul>

              <Link
                href="/sign-up-login"
                className="block text-center py-3.5 rounded-full font-bold text-sm transition-all border"
                style={{
                  borderColor: 'rgba(201,169,110,0.5)',
                  color: isDark ? '#C9A96E' : '#8B4513',
                  background: isDark ? 'rgba(201,169,110,0.08)' : 'rgba(139,69,19,0.06)',
                }}
              >
                Get started monthly
              </Link>
            </div>

            {/* Annual plan */}
            <div
              className="rounded-2xl p-8 flex flex-col relative overflow-hidden"
              style={{
                background: isDark
                  ? 'linear-gradient(145deg, rgba(201,169,110,0.18) 0%, rgba(139,69,19,0.25) 100%)'
                  : 'linear-gradient(145deg, rgba(201,169,110,0.15) 0%, rgba(139,69,19,0.18) 100%)',
                border: '1.5px solid rgba(201,169,110,0.5)',
                boxShadow: isDark ? '0 0 60px rgba(201,169,110,0.15)' : '0 0 40px rgba(201,169,110,0.12)',
                opacity: plansVisible ? 1 : 0,
                transform: plansVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s',
              }}
            >
              <div
                className="absolute top-5 right-5 px-3 py-1 rounded-full text-xs font-bold"
                style={{ background: 'linear-gradient(135deg, #C9A96E, #8B4513)', color: '#F5F0E8' }}
              >
                2 months FREE
              </div>

              <div className="mb-6">
                <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C9A96E' }}>Annual</p>
                <div className="flex items-end gap-1.5">
                  <span className="font-bold" style={{ fontFamily: 'var(--font-serif)', fontSize: '3.5rem', color: textBase, lineHeight: 1 }}>$299</span>
                  <span className="text-base mb-2" style={{ color: textMuted }}>/year</span>
                </div>
              </div>

              <ul className="space-y-3 flex-1 mb-8">
                {FEATURES?.map((f, i) => (
                  <li key={`af-${i}`} className="flex items-center gap-3 text-sm" style={{ color: isDark ? 'rgba(240,235,227,0.9)' : 'rgba(44,24,16,0.85)' }}>
                    <span className="text-base" style={{ color: '#D4AF37' }}>{f?.icon}</span>
                    {f?.text}
                  </li>
                ))}
              </ul>

              <Link
                href="/sign-up-login"
                className="block text-center py-3.5 rounded-full font-bold text-sm transition-all"
                style={{
                  background: 'linear-gradient(135deg, #C9A96E, #8B4513)',
                  color: '#F5F0E8',
                  boxShadow: '0 6px 24px rgba(201,169,110,0.4)',
                }}
              >
                Get started annually →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t py-8 text-center" style={{ borderColor: cardBorder }}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <AppLogo size={20} />
          <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-serif)' }}>
            <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>
          </span>
        </div>
        <p className="text-xs mb-4" style={{ color: textMuted }}>© 2026 <span style={{ color: '#C9A96E' }}>Sched</span><span style={{ color: '#8B4513' }}>sure</span>. All rights reserved.</p>
        <div className="flex items-center justify-center gap-4 text-xs flex-wrap" style={{ color: textMuted }}>
          <span>Need help?</span>
          <a
            href="mailto:support@schedsure.com"
            className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-70"
            style={{ color: isDark ? '#C9A96E' : '#8B4513' }}
          >
            Contact Support
          </a>
          <Link
            href="/terms"
            className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-70"
            style={{ color: isDark ? '#C9A96E' : '#8B4513' }}
          >
            Terms & Conditions
          </Link>
        </div>
      </footer>

      <style>{`
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(3vw, 4vw) scale(1.05); }
          66% { transform: translate(-2vw, 2vw) scale(0.97); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40% { transform: translate(-4vw, -3vw) scale(1.08); }
          70% { transform: translate(2vw, -1vw) scale(0.95); }
        }
        @keyframes shimmerTitle {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </div>
  );
}

export default function LandingPage() {
  return (
    <Suspense fallback={null}>
      <LandingPageInner />
    </Suspense>
  );
}
